import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  FileArrowUp,
  Package,
  Tag,
  WarningCircle,
  CheckCircle,
  Archive,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';
import { apiClient, Product, ProductStatus, MERCHANT_ROUTES, useAuthStore } from '@dukene/shared';
import ProductImportModal from '../../components/products/ProductImport';
import { confirmAction } from '../../components/modals/ConfirmModal';

interface ProductsResponse {
  products: Product[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

export function ProductsSubNav() {
  const location = useLocation();
  const currentPath = location.pathname;

  const tabs = [
    { label: 'All Products', href: MERCHANT_ROUTES.PRODUCTS, icon: Package },
    { label: 'Categories', href: MERCHANT_ROUTES.CATEGORIES, icon: Tag },
    { label: 'Inventory & Stock', href: MERCHANT_ROUTES.INVENTORY, icon: CheckCircle },
  ];

  return (
    <div className="flex items-center gap-1.5 border-b border-[#E1E4E8] dark:border-slate-800 pb-2 mb-4 overflow-x-auto">
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = currentPath === t.href;
        return (
          <Link
            key={t.href}
            to={t.href}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors whitespace-nowrap ${
              isActive
                ? 'bg-[#006838] text-white shadow-xs'
                : 'text-[#5C5F62] dark:text-slate-400 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 hover:text-[#202223] dark:hover:text-white'
            }`}
          >
            <Icon weight="bold" className="w-3.5 h-3.5" />
            {t.label}
          </Link>
        );
      })}
    </div>
  );
}

export default function Products() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProductStatus | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  const { data: productsData, error, isLoading } = useQuery<ProductsResponse>({
    queryKey: ['merchant-products', activeStoreId, statusFilter, categoryFilter, searchQuery, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (categoryFilter !== 'ALL') params.append('categoryId', categoryFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', page.toString());
      params.append('limit', '20');

      return apiClient.get<ProductsResponse>(`/products?${params}`);
    },
    enabled: !!activeStoreId,
  });

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories', activeStoreId],
    queryFn: async () => {
      return apiClient.get<Category[]>('/products/categories');
    },
    enabled: !!activeStoreId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (productId: string) => {
      return apiClient.delete(`/products/${productId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-products', activeStoreId] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to delete product');
    },
  });

  const handleDelete = async (productId: string) => {
    const confirmed = await confirmAction({
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;
    deleteMutation.mutate(productId);
  };

  const products = productsData?.products || [];
  const pagination = productsData?.pagination;

  const getStatusBadge = (status: ProductStatus) => {
    if (status === ProductStatus.ACTIVE) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
          <CheckCircle weight="fill" className="w-3 h-3" />
          Active
        </span>
      );
    }
    if (status === ProductStatus.DRAFT) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <WarningCircle weight="fill" className="w-3 h-3" />
          Draft
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-300">
        <Archive weight="fill" className="w-3 h-3" />
        Archived
      </span>
    );
  };

  const getStockBadge = (inventory: Product['inventory']) => {
    if (!inventory?.trackQuantity) {
      return (
        <span className="inline-block bg-gray-100 text-gray-600 rounded px-2 py-0.5 text-[10px] font-semibold">
          Untracked
        </span>
      );
    }

    if (inventory.available === 0) {
      return (
        <span className="inline-block bg-red-50 text-red-700 border border-red-200 rounded px-2 py-0.5 text-[10px] font-bold">
          Out of Stock
        </span>
      );
    }

    if (inventory.lowStockThreshold && inventory.available <= inventory.lowStockThreshold) {
      return (
        <span className="inline-block bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5 text-[10px] font-bold">
          {inventory.available} left (Low)
        </span>
      );
    }

    return (
      <span className="inline-block bg-[#E3F1DF] text-[#006838] rounded px-2 py-0.5 text-[10px] font-bold">
        {inventory.available} in stock
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl p-4 text-red-800 dark:text-red-300 text-xs font-semibold">
        Failed to load product catalog records. Please retry or refresh the page.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ProductsSubNav />

      {/* High-density Polaris Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Product Catalog
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors"
          >
            <FileArrowUp weight="bold" className="w-3.5 h-3.5 text-[#006838]" />
            Import CSV
          </button>
          <Link to={MERCHANT_ROUTES.PRODUCTS_NEW}>
            <button className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors">
              <Plus weight="bold" className="w-3.5 h-3.5" />
              Add Product
            </button>
          </Link>
        </div>
      </div>

      {/* Filters & Table Container */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {/* Filter Toolbar */}
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2 relative">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
            />
            <input
              type="text"
              placeholder="Filter products by name or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ProductStatus | 'ALL')}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Statuses</option>
              <option value={ProductStatus.ACTIVE}>Active</option>
              <option value={ProductStatus.DRAFT}>Draft</option>
              <option value={ProductStatus.ARCHIVED}>Archived</option>
            </select>
          </div>

          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Categories</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table Content */}
        {isLoading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Package weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Products Found
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
              {searchQuery || statusFilter !== 'ALL' || categoryFilter !== 'ALL'
                ? 'Try clearing your search filters to show all catalog products.'
                : 'Get started by adding your first product SKU.'}
            </p>
            {!searchQuery && statusFilter === 'ALL' && categoryFilter === 'ALL' && (
              <div className="mt-4">
                <Link to={MERCHANT_ROUTES.PRODUCTS_NEW}>
                  <button className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs">
                    <Plus weight="bold" className="w-3.5 h-3.5" />
                    Add Product
                  </button>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Product</th>
                  <th className="py-2.5 px-4">SKU</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4">Stock Level</th>
                  <th className="py-2.5 px-4 text-right">Price</th>
                  <th className="py-2.5 px-4">Categories</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.images?.[0]?.url || '/placeholder-product.jpg'}
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded-lg border border-[#E1E4E8] dark:border-slate-700 shrink-0"
                        />
                        <div className="min-w-0">
                          <Link
                            to={`${MERCHANT_ROUTES.PRODUCTS}/${product.id}/edit`}
                            className="font-semibold text-[#202223] dark:text-white hover:text-[#006838] transition-colors block truncate max-w-xs"
                          >
                            {product.name}
                          </Link>
                          {product.shortDescription && (
                            <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 truncate max-w-xs mt-0.5">
                              {product.shortDescription}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <code className="bg-[#E3F1DF] px-2 py-0.5 rounded text-[11px] text-[#006838] font-mono font-bold">
                        {product.sku || 'N/A'}
                      </code>
                    </td>

                    <td className="py-3 px-4">{getStatusBadge(product.status)}</td>

                    <td className="py-3 px-4">{getStockBadge(product.inventory)}</td>

                    <td className="py-3 px-4 text-right font-mono font-bold text-[#006838]">
                      KES {Number(product.price || 0).toLocaleString()}
                    </td>

                    <td className="py-3 px-4">
                      {product.categories && product.categories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {product.categories.map((cat) => (
                            <span
                              key={cat.id}
                              className="bg-[#F6F6F7] dark:bg-slate-800 text-[#202223] dark:text-slate-300 rounded px-2 py-0.5 text-[10px] font-semibold border border-[#D2D5D9] dark:border-slate-700"
                            >
                              {cat.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[#8C9196] text-[11px] italic">Uncategorized</span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Link
                          to={`${MERCHANT_ROUTES.PRODUCTS}/${product.id}/edit`}
                          className="p-1 rounded hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
                          title="Edit product"
                        >
                          <PencilSimple weight="bold" className="w-4 h-4" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(product.id)}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 transition-colors"
                          title="Delete product"
                        >
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="border-t border-[#E1E4E8] dark:border-slate-800 px-4 py-3 flex items-center justify-between text-xs text-[#5C5F62]">
            <span>
              Showing {(page - 1) * 20 + 1} to{' '}
              {Math.min(page * 20, pagination.total)} of {pagination.total} products
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-7 px-3 rounded border border-[#D2D5D9] bg-white hover:bg-[#F6F6F7] disabled:opacity-50 font-semibold text-xs inline-flex items-center gap-1"
              >
                <CaretLeft weight="bold" className="w-3.5 h-3.5" />
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.pages}
                className="h-7 px-3 rounded border border-[#D2D5D9] bg-white hover:bg-[#F6F6F7] disabled:opacity-50 font-semibold text-xs inline-flex items-center gap-1"
              >
                Next
                <CaretRight weight="bold" className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {importOpen && (
        <ProductImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => {
            setImportOpen(false);
            queryClient.invalidateQueries({ queryKey: ['merchant-products', activeStoreId] });
          }}
        />
      )}
    </div>
  );
}
